// Relay client (ARCHITECTURE.md §7). Talks to a blind mailbox relay: it
// authenticates by signing a challenge with the identity's signing key, then
// publishes and fetches encrypted envelopes. The relay never sees plaintext.
//
// The relay is addressed by URL (federation-ready). connect-src in the CSP must
// include the relay origin at deploy time.

import { sign } from './sign';
import { utf8ToBytes, bytesToBase64url } from './encoding';
import type { Envelope } from './envelope';

export interface RelayIdentity {
  circleId: string;
  keys: { signPrivate: CryptoKey };
}

interface Token {
  token: string;
  expiresAt: number;
}

/** The caller's own mailbox view (blind-safe: never anyone else's graph). */
export interface RelayStatus {
  inbox: number;
  authors: string[];
}

/** An HTTP error from the relay, carrying the status so callers can tell a
 * benign outcome (409: recipient already has this version) from a real
 * failure. */
class RelayHttpError extends Error {
  constructor(
    public status: number,
    message: string,
  ) {
    super(message);
  }
}

async function asJson(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!res.ok) {
    let message = `Relais: ${res.status}`;
    try {
      const parsed = JSON.parse(text) as { error?: string };
      if (parsed.error) message = parsed.error;
    } catch {
      /* keep default */
    }
    throw new RelayHttpError(res.status, message);
  }
  return text ? JSON.parse(text) : {};
}

export class RelayClient {
  private token: Token | null = null;
  private tokenFor: string | null = null; // Circle ID the cached token belongs to

  constructor(private baseUrl: string) {}

  get configured(): boolean {
    return this.baseUrl.length > 0;
  }

  private url(path: string): string {
    return this.baseUrl.replace(/\/$/, '') + path;
  }

  private async ensureToken(id: RelayIdentity): Promise<string> {
    if (this.tokenFor === id.circleId && this.token && this.token.expiresAt - Date.now() > 60_000) {
      return this.token.token;
    }

    const challengeRes = (await asJson(
      await fetch(this.url('/v1/auth/challenge'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ circleId: id.circleId }),
      }),
    )) as { challenge: string };

    const signature = bytesToBase64url(await sign(id.keys.signPrivate, utf8ToBytes(challengeRes.challenge)));

    const verifyRes = (await asJson(
      await fetch(this.url('/v1/auth/verify'), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ circleId: id.circleId, challenge: challengeRes.challenge, signature }),
      }),
    )) as Token;

    this.token = verifyRes;
    this.tokenFor = id.circleId;
    return verifyRes.token;
  }

  /**
   * Publish one or more envelopes (already built for their recipients). One
   * call always carries a mix of "new update" and "this recipient already has
   * my current version" envelopes — publishing to all friends resends to
   * everyone, not just whoever changed. The relay reports the latter as 409,
   * which is not a failure to act on: skip it and keep going, so one already
   * up-to-date friend earlier in the list can never block a new one later in
   * the same batch.
   */
  async publish(id: RelayIdentity, envelopes: Envelope[]): Promise<void> {
    const token = await this.ensureToken(id);
    for (const env of envelopes) {
      try {
        await asJson(
          await fetch(this.url('/v1/envelopes'), {
            method: 'PUT',
            headers: { 'content-type': 'application/json', authorization: `Bearer ${token}` },
            body: JSON.stringify(env),
          }),
        );
      } catch (err) {
        if (err instanceof RelayHttpError && err.status === 409) continue;
        throw err;
      }
    }
  }

  /** Fetch all envelopes addressed to me. */
  async fetchInbox(id: RelayIdentity): Promise<Envelope[]> {
    const token = await this.ensureToken(id);
    const res = (await asJson(
      await fetch(this.url('/v1/envelopes'), { headers: { authorization: `Bearer ${token}` } }),
    )) as { envelopes: Envelope[] };
    return res.envelopes ?? [];
  }

  /** The authenticated caller's own mailbox status (blind-safe). */
  async status(id: RelayIdentity): Promise<RelayStatus> {
    const token = await this.ensureToken(id);
    const res = (await asJson(
      await fetch(this.url('/v1/status'), { headers: { authorization: `Bearer ${token}` } }),
    )) as RelayStatus;
    return { inbox: res.inbox ?? 0, authors: res.authors ?? [] };
  }

  /**
   * Subscribe to live delivery over WebSocket. Returns a function to close it.
   * Reconnection/backoff is the caller's concern.
   */
  async subscribe(id: RelayIdentity, onEnvelope: (env: Envelope) => void): Promise<() => void> {
    const token = await this.ensureToken(id);
    const wsUrl = this.url('/v1/subscribe').replace(/^http/, 'ws') + `?token=${encodeURIComponent(token)}`;
    const ws = new WebSocket(wsUrl);
    ws.addEventListener('message', (ev: MessageEvent) => {
      try {
        const msg = JSON.parse(String(ev.data)) as { type?: string; envelope?: Envelope };
        if (msg.type === 'envelope' && msg.envelope) onEnvelope(msg.envelope);
      } catch {
        /* ignore malformed frames */
      }
    });
    return () => ws.close();
  }
}
